import { useTranslation } from "react-i18next";

/**
 * Horizontal schematic of "the engine" for the /about Why-us section:
 * idea → engine block (pipeline / foundation / agents) → human review gate
 * → production. Shares the visual language of the case-study pipeline
 * diagrams (surface nodes, dashed connectors, accent arrows, mono labels).
 * Scrolls horizontally inside its own container on small screens, like the
 * other diagrams.
 */
export function EngineDiagram() {
  const { t } = useTranslation();
  const parts = t("about.tooling.diagram.parts", {
    returnObjects: true,
  }) as string[];

  const mono = "var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)";
  const labelProps = {
    fontFamily: mono,
    fontSize: 11,
    letterSpacing: "0.14em",
    textAnchor: "middle" as const,
  };

  // Block spans x 150–670; three 146-wide bars with 12px gaps total 462,
  // so 29px padding each side centers them: 150 + 29 = 179.
  const BAR_XS = [179, 337, 495];
  const BAR_W = 146;

  // Split a part label into at most two lines (first word / rest) so it
  // fits the narrow bars.
  const partLines = (label: string): string[] => {
    const words = label.toUpperCase().split(" ");
    if (words.length === 1) return words;
    return [words[0]!, words.slice(1).join(" ")];
  };

  return (
    <div className="-mx-6 overflow-x-auto sm:mx-0">
      <div className="min-w-[1020px] px-6 sm:min-w-0 sm:px-0">
        <svg
          viewBox="0 0 1010 212"
          className="h-auto w-full"
          role="img"
          aria-label={t("about.tooling.diagram.srDescription")}
        >
          <defs>
            <marker
              id="engine-arrow"
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--color-accent)" />
            </marker>
          </defs>

          {/* Input: idea */}
          <rect
            x="10"
            y="65"
            width="110"
            height="40"
            rx="20"
            fill="var(--color-surface)"
            stroke="var(--color-line-strong)"
            strokeWidth="1.2"
          />
          <text {...labelProps} x="65" y="89" fill="var(--color-ink)">
            {t("about.tooling.diagram.input").toUpperCase()}
          </text>

          <line
            x1="126"
            y1="85"
            x2="144"
            y2="85"
            stroke="var(--color-ink-3)"
            strokeWidth="1.4"
            strokeDasharray="4 5"
            markerEnd="url(#engine-arrow)"
          />

          {/* Engine block */}
          <rect x="150" y="18" width="520" height="134" rx="18" fill="var(--color-ink)" />
          <text {...labelProps} x="410" y="42" fill="var(--color-accent)" fontSize={10}>
            {t("about.tooling.diagram.engine").toUpperCase()}
          </text>
          {parts.map((part, i) => {
            const barX = BAR_XS[i]!;
            const cx = barX + BAR_W / 2;
            const lines = partLines(part);
            return (
              <g key={part}>
                <rect
                  x={barX}
                  y="56"
                  width={BAR_W}
                  height="76"
                  rx="10"
                  fill="var(--color-page-bg)"
                  fillOpacity="0.08"
                  stroke="var(--color-page-bg)"
                  strokeOpacity="0.22"
                  strokeWidth="1"
                />
                {lines.map((line, j) => (
                  <text
                    key={line}
                    {...labelProps}
                    x={cx}
                    y={lines.length === 1 ? 99 : 90 + j * 17}
                    fill="var(--color-page-bg)"
                  >
                    {line}
                  </text>
                ))}
              </g>
            );
          })}

          <line
            x1="676"
            y1="85"
            x2="694"
            y2="85"
            stroke="var(--color-ink-3)"
            strokeWidth="1.4"
            strokeDasharray="4 5"
            markerEnd="url(#engine-arrow)"
          />

          {/* Human review gate */}
          <rect
            x="700"
            y="63"
            width="160"
            height="44"
            rx="12"
            fill="var(--color-surface)"
            stroke="var(--color-accent)"
            strokeWidth="1.4"
          />
          <text {...labelProps} x="780" y="89" fill="var(--color-accent)">
            {t("about.tooling.diagram.gate").toUpperCase()}
          </text>

          <line
            x1="866"
            y1="85"
            x2="884"
            y2="85"
            stroke="var(--color-ink-3)"
            strokeWidth="1.4"
            strokeDasharray="4 5"
            markerEnd="url(#engine-arrow)"
          />

          {/* Feedback loop: review → idea (iterate) */}
          <path
            d="M 780 107 L 780 193 L 65 193 L 65 111"
            fill="none"
            stroke="var(--color-ink-3)"
            strokeWidth="1.4"
            strokeDasharray="4 5"
            markerEnd="url(#engine-arrow)"
          />
          <text {...labelProps} x="422" y="186" fill="var(--color-ink-3)" fontSize={10}>
            {t("about.tooling.diagram.loop").toUpperCase()}
          </text>

          {/* Output: production */}
          <rect x="890" y="65" width="112" height="40" rx="20" fill="var(--color-accent)" />
          <text {...labelProps} x="946" y="89" fill="var(--color-page-bg)" fontSize={10}>
            {t("about.tooling.diagram.output").toUpperCase()}
          </text>
        </svg>
      </div>
    </div>
  );
}
